'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

type UserType = 'student' | 'college' | 'other';

export default function SignupPage() {
    const [isLoading, setIsLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedUserType, setSelectedUserType] = useState<UserType>('student');
    const router = useRouter();

    const handleUserTypeClick = (type: UserType) => {
        console.log('User type clicked:', type);
        setSelectedUserType(type);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        if (currentStep === 1) {
            // Move to step 2
            setTimeout(() => {
                setIsLoading(false);
                setCurrentStep(2);
            }, 1000);
        } else {
            // Complete signup
            setTimeout(() => {
                setIsLoading(false);
                router.push('/for-students/dashboard');
            }, 2000);
        }
    };

    const goBackToStep1 = () => {
        setCurrentStep(1);
    };

    return (
        <div className="min-h-screen pt-20 flex bg-background relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-success/20 rounded-full blur-[100px] animate-pulse" />
                <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary/20 rounded-full blur-[100px] animate-pulse delay-1000" />
            </div>

            <div className="w-full max-w-7xl mx-auto flex flex-col lg:flex-row-reverse items-center justify-center gap-8 lg:gap-16 px-4 sm:px-6 lg:px-8 py-12 relative z-10">
                {/* Right Side - Visual Content */}
                <div className="hidden lg:flex flex-col flex-1 items-end justify-center space-y-8 text-right">
                    <div className="relative">
                        <div className="absolute -inset-4 bg-gradient-to-r from-success to-primary opacity-30 blur-lg rounded-full" />
                        <h1 className="relative text-5xl font-bold tracking-tight text-white leading-tight">
                            Start Your Journey <br />
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-success via-primary to-accent">
                                With AstikLearn
                            </span>
                        </h1>
                    </div>

                    <p className="text-xl text-text-secondary leading-relaxed max-w-lg">
                        Join thousands of students and institutions transforming education. Create your account today and unlock a world of possibilities.
                    </p>

                    <div className="grid grid-cols-2 gap-6 w-full max-w-lg">
                        <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm text-left">
                            <Icon name="UserGroupIcon" size={32} className="text-success mb-3" />
                            <h3 className="text-white font-semibold mb-1">Community</h3>
                            <p className="text-sm text-text-secondary">Join a global network of peers</p>
                        </div>
                        <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm text-left">
                            <Icon name="GlobeAltIcon" size={32} className="text-primary mb-3" />
                            <h3 className="text-white font-semibold mb-1">Global Access</h3>
                            <p className="text-sm text-text-secondary">Learn from anywhere, anytime</p>
                        </div>
                    </div>
                </div>

                {/* Left Side - Signup Form */}
                <div className="w-full max-w-md flex-1">
                    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-card">
                        {/* Step Indicator */}
                        <div className="flex items-center justify-center gap-2 mb-6">
                            <div className={`h-2 w-16 rounded-full transition-all duration-300 ${currentStep === 1 ? 'bg-primary' : 'bg-primary/30'
                                }`} />
                            <div className={`h-2 w-16 rounded-full transition-all duration-300 ${currentStep === 2 ? 'bg-primary' : 'bg-white/10'
                                }`} />
                        </div>

                        <div className="mb-8 text-center lg:text-left">
                            <h2 className="text-3xl font-bold text-white mb-2">
                                {currentStep === 1 ? 'Create Account' : 'Tell us more'}
                            </h2>
                            <p className="text-text-secondary">
                                {currentStep === 1 ? (
                                    <>
                                        Already have an account?{' '}
                                        <Link href="/for-students/login" className="text-primary hover:text-accent transition-colors font-medium">
                                            Log in
                                        </Link>
                                    </>
                                ) : 'Help us personalize your experience'}
                            </p>
                        </div>

                        {/* STEP 1: Basic Info */}
                        {currentStep === 1 && (
                            <>
                                {/* User Type Selection */}
                                <div className="mb-3 text-sm font-semibold text-white text-center lg:text-left">
                                    I am a...
                                </div>
                                <div className="flex gap-3 mb-6">
                                    <button
                                        type="button"
                                        onClick={() => handleUserTypeClick('student')}
                                        className={`flex-1 py-3 px-4 text-sm font-bold rounded-xl transition-all duration-300 border-2 ${selectedUserType === 'student'
                                            ? 'bg-primary border-primary text-white shadow-lg shadow-primary/30 scale-105'
                                            : 'bg-white/5 border-white/20 text-text-secondary hover:text-white hover:border-white/40'
                                            }`}
                                    >
                                        Student
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleUserTypeClick('college')}
                                        className={`flex-1 py-3 px-4 text-sm font-bold rounded-xl transition-all duration-300 border-2 ${selectedUserType === 'college'
                                            ? 'bg-primary border-primary text-white shadow-lg shadow-primary/30 scale-105'
                                            : 'bg-white/5 border-white/20 text-text-secondary hover:text-white hover:border-white/40'
                                            }`}
                                    >
                                        College
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleUserTypeClick('other')}
                                        className={`flex-1 py-3 px-4 text-sm font-bold rounded-xl transition-all duration-300 border-2 ${selectedUserType === 'other'
                                            ? 'bg-primary border-primary text-white shadow-lg shadow-primary/30 scale-105'
                                            : 'bg-white/5 border-white/20 text-text-secondary hover:text-white hover:border-white/40'
                                            }`}
                                    >
                                        Other
                                    </button>
                                </div>
                            </>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* STEP 1 FIELDS */}
                            {currentStep === 1 && (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label htmlFor="step1-firstName" className="block text-sm font-medium text-text-secondary">
                                                First Name
                                            </label>
                                            <input
                                                id="step1-firstName"
                                                name="firstName"
                                                type="text"
                                                required
                                                className="block w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-300"
                                                placeholder="John"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label htmlFor="step1-lastName" className="block text-sm font-medium text-text-secondary">
                                                Last Name
                                            </label>
                                            <input
                                                id="step1-lastName"
                                                name="lastName"
                                                type="text"
                                                required
                                                className="block w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-300"
                                                placeholder="Doe"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="step1-email" className="block text-sm font-medium text-text-secondary">
                                            Email Address
                                        </label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-text-tertiary group-focus-within:text-primary transition-colors">
                                                <Icon name="EnvelopeIcon" size={20} />
                                            </div>
                                            <input
                                                id="step1-email"
                                                name="email"
                                                type="email"
                                                required
                                                className="block w-full pl-10 pr-3 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-300"
                                                placeholder="you@example.com"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="step1-password" className="block text-sm font-medium text-text-secondary">
                                            Password
                                        </label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-text-tertiary group-focus-within:text-primary transition-colors">
                                                <Icon name="LockClosedIcon" size={20} />
                                            </div>
                                            <input
                                                id="step1-password"
                                                name="password"
                                                type="password"
                                                required
                                                className="block w-full pl-10 pr-3 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-300"
                                                placeholder="••••••••"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="step1-confirmPassword" className="block text-sm font-medium text-text-secondary">
                                            Confirm Password
                                        </label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-text-tertiary group-focus-within:text-primary transition-colors">
                                                <Icon name="LockClosedIcon" size={20} />
                                            </div>
                                            <input
                                                id="step1-confirmPassword"
                                                name="confirmPassword"
                                                type="password"
                                                required
                                                className="block w-full pl-10 pr-3 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-300"
                                                placeholder="••••••••"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 pt-2">
                                        <input type="checkbox" id="step1-terms" name="terms" className="rounded bg-white/5 border-white/10 text-primary focus:ring-primary/50" required />
                                        <label htmlFor="step1-terms" className="text-sm text-text-secondary">
                                            I agree to the <Link href="#" className="text-white hover:text-primary underline decoration-dotted">Terms of Service</Link> and <Link href="#" className="text-white hover:text-primary underline decoration-dotted">Privacy Policy</Link>
                                        </label>
                                    </div>
                                </>
                            )}

                            {/* STEP 2 FIELDS */}
                            {currentStep === 2 && (
                                <>
                                    <div className="space-y-2">
                                        <label htmlFor="step2-college" className="block text-sm font-medium text-text-secondary">
                                            Current College / Institution
                                        </label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-text-tertiary group-focus-within:text-primary transition-colors">
                                                <Icon name="AcademicCapIcon" size={20} />
                                            </div>
                                            <input
                                                id="step2-college"
                                                name="college"
                                                type="text"
                                                required
                                                className="block w-full pl-10 pr-3 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-300"
                                                placeholder="e.g. Indian Institute of Technology"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="step2-year" className="block text-sm font-medium text-text-secondary">
                                            Year of Study
                                        </label>
                                        <select
                                            id="step2-year"
                                            name="year"
                                            required
                                            className="block w-full px-3 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-300 [&>option]:bg-background"
                                        >
                                            <option value="">Select Year</option>
                                            <option value="1">1st Year</option>
                                            <option value="2">2nd Year</option>
                                            <option value="3">3rd Year</option>
                                            <option value="4">4th Year</option>
                                            <option value="other">Other / Graduated</option>
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="step2-goals" className="block text-sm font-medium text-text-secondary">
                                            What are you looking for?
                                        </label>
                                        <select
                                            id="step2-goals"
                                            name="goals"
                                            required
                                            className="block w-full px-3 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all duration-300 [&>option]:bg-background"
                                        >
                                            <option value="">Select Goal</option>
                                            <option value="upskill">Upskilling & New Tech</option>
                                            <option value="placement">Placement Preparation</option>
                                            <option value="internship">Finding Internships</option>
                                            <option value="community">Joining Community</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-4 pt-4">
                                {currentStep === 2 && (
                                    <button
                                        type="button"
                                        onClick={goBackToStep1}
                                        className="w-1/3 px-4 py-3.5 rounded-xl border border-white/10 text-white font-medium hover:bg-white/5 transition-all duration-300"
                                    >
                                        Back
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="relative flex-1 group overflow-hidden rounded-xl bg-gradient-to-r from-success via-primary to-accent p-[1px] focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 focus:ring-offset-background"
                                >
                                    <div className="relative w-full h-full bg-background/50 group-hover:bg-transparent transition-colors duration-300 rounded-[11px] px-8 py-3.5">
                                        <span className="flex items-center justify-center gap-2 text-white font-bold tracking-wide">
                                            {isLoading ? 'Processing...' : (currentStep === 1 ? 'Next Step' : 'Complete Signup')}
                                            {!isLoading && <Icon name="ArrowRightIcon" size={18} className="group-hover:translate-x-1 transition-transform" />}
                                        </span>
                                    </div>
                                </button>
                            </div>
                        </form>

                        {/* Social Login - Only on Step 1 */}
                        {currentStep === 1 && (
                            <div className="mt-8">
                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-white/10" />
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-2 bg-background text-text-tertiary">Or sign up with</span>
                                    </div>
                                </div>

                                <div className="mt-6 grid grid-cols-2 gap-4">
                                    <button type="button" className="flex items-center justify-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all duration-300 group">
                                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                        </svg>
                                        <span className="text-sm font-medium text-white group-hover:text-primary transition-colors">Google</span>
                                    </button>
                                    <button type="button" className="flex items-center justify-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all duration-300 group">
                                        <svg className="w-5 h-5 text-white group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                                            <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-sm font-medium text-white group-hover:text-primary transition-colors">GitHub</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
